import initSqlJs from "sql.js";
import { openDB } from "idb";

const DB_NAME = "datapilot_local_db";
const DB_VERSION = 4;
const DB_STORE_SQLITE = "sqlite_file";
const DB_STORE_HISTORY = "query_history";
const DB_STORE_SAVED = "saved_queries";

let SQL = null;
let db = null;

async function loadInitialSchema(databaseInstance) {
  try {
    const scriptTag = document.getElementById('base-schema-sql');
    if (!scriptTag) {
      throw new Error("base-schema-sql script tag not found in index.html");
    }
    const schemaSql = scriptTag.textContent;
    databaseInstance.exec(preprocessQuery(schemaSql));
    console.log("Initial schema loaded successfully from script tag.");
  } catch (error) {
    console.error("Error loading initial schema from script tag:", error);
    // Potentially re-throw or handle more gracefully
  }
}

export function ensureSavedQueriesSqliteTable(databaseInstance = db) {
  if (!databaseInstance) return;
  try {
    databaseInstance.exec(`
      CREATE TABLE IF NOT EXISTS _datapilot_saved_queries (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        query TEXT NOT NULL,
        label TEXT,
        saved_at TEXT
      );
    `);
  } catch (err) {
    console.error("Error creating _datapilot_saved_queries table in SQLite:", err);
  }
}

export async function loadDatabase(forceNew = false, initialData = null) {
  if (!SQL) {
    SQL = await initSqlJs({
      locateFile: file => `https://sql.js.org/dist/${file}`
    });
  }

  // Open IndexedDB
  const idb = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(DB_STORE_SQLITE)) {
        db.createObjectStore(DB_STORE_SQLITE);
      }
      if (!db.objectStoreNames.contains(DB_STORE_HISTORY)) {
        db.createObjectStore(DB_STORE_HISTORY, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(DB_STORE_SAVED)) {
        db.createObjectStore(DB_STORE_SAVED, { keyPath: 'id', autoIncrement: true });
      }
    },
  });

  if (forceNew) {
    db = new SQL.Database(); // Always create a fresh DB if forceNew is true
    await idb.clear(DB_STORE_SQLITE); // Clear existing data in IndexedDB
    await loadInitialSchema(db); // Load initial schema for fresh DB
  } else if (initialData) {
    db = new SQL.Database(initialData); // Load from provided initial data
  } else {
    const stored = await idb.get(DB_STORE_SQLITE, "db");
    if (stored) {
      db = new SQL.Database(new Uint8Array(stored));
    } else {
      db = new SQL.Database(); // fresh DB if no stored data
      await loadInitialSchema(db); // Load initial schema for fresh DB
    }
  }

  ensureSavedQueriesSqliteTable(db);
  return db;
}

export async function saveDatabase() {
  if (!db) return;
  ensureSavedQueriesSqliteTable(db);
  const binary = db.export();
  const idb = await openDB(DB_NAME, DB_VERSION);
  await idb.put(DB_STORE_SQLITE, binary, "db");
}

function translateMysqlToSqlite(query) {
  if (!query) return query;
  let q = query.trim();
  const upper = q.toUpperCase();

  // 1. Handle MySQL SHOW TABLES
  if (/^SHOW\s+TABLES\s*;?$/i.test(q)) {
    return "SELECT name AS Tables FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%';";
  }

  // 2. Handle MySQL DESCRIBE / DESC / SHOW COLUMNS FROM <table>
  const descMatch = q.match(/^(?:DESCRIBE|DESC)\s+[`"']?(\w+)[`"']?\s*;?$/i);
  if (descMatch) {
    return `PRAGMA table_info('${descMatch[1]}');`;
  }

  const showColsMatch = q.match(/^SHOW\s+COLUMNS\s+FROM\s+[`"']?(\w+)[`"']?\s*;?$/i);
  if (showColsMatch) {
    return `PRAGMA table_info('${showColsMatch[1]}');`;
  }

  // 3. Handle SHOW CREATE TABLE <table>
  const showCreateMatch = q.match(/^SHOW\s+CREATE\s+TABLE\s+[`"']?(\w+)[`"']?\s*;?$/i);
  if (showCreateMatch) {
    return `SELECT sql FROM sqlite_master WHERE type='table' AND name='${showCreateMatch[1]}';`;
  }

  // 4. Handle MySQL SET statements
  if (/^SET\s+FOREIGN_KEY_CHECKS\s*=\s*0/i.test(q)) {
    return "PRAGMA foreign_keys = OFF;";
  }
  if (/^SET\s+FOREIGN_KEY_CHECKS\s*=\s*1/i.test(q)) {
    return "PRAGMA foreign_keys = ON;";
  }

  // 5. Handle MySQL CREATE TABLE transformations
  if (upper.includes('CREATE TABLE')) {
    // 1. Convert any AUTO_INCREMENT + PRIMARY KEY column definition -> INTEGER PRIMARY KEY AUTOINCREMENT
    q = q.replace(/([`"'\w]+)\s+(?:TINYINT|SMALLINT|MEDIUMINT|INT|INTEGER|BIGINT)\b[^,\)]*?\bAUTO_?INCREMENT\b[^,\)]*?\bPRIMARY\s+KEY\b[^,\)]*/gi, '$1 INTEGER PRIMARY KEY AUTOINCREMENT');
    q = q.replace(/([`"'\w]+)\s+(?:TINYINT|SMALLINT|MEDIUMINT|INT|INTEGER|BIGINT)\b[^,\)]*?\bPRIMARY\s+KEY\b[^,\)]*?\bAUTO_?INCREMENT\b[^,\)]*/gi, '$1 INTEGER PRIMARY KEY AUTOINCREMENT');

    // 2. Convert any AUTO_INCREMENT column definition -> INTEGER PRIMARY KEY AUTOINCREMENT
    q = q.replace(/([`"'\w]+)\s+(?:TINYINT|SMALLINT|MEDIUMINT|INT|INTEGER|BIGINT)\b[^,\)]*?\bAUTO_?INCREMENT\b[^,\)]*/gi, '$1 INTEGER PRIMARY KEY AUTOINCREMENT');

    // 3. Strip any remaining orphan AUTO_INCREMENT / AUTOINCREMENT keywords
    q = q.replace(/\bAUTO_?INCREMENT\b/gi, '');

    // Convert ENUM(...) to TEXT
    q = q.replace(/ENUM\s*\((?:[^()]+|\([^()]*\))*\)/gi, 'TEXT');

    // Convert MySQL data types
    q = q.replace(/\b(?:LONGTEXT|MEDIUMTEXT|TINYTEXT)\b/gi, 'TEXT');
    q = q.replace(/\b(?:LONGBLOB|MEDIUMBLOB|TINYBLOB)\b/gi, 'BLOB');

    // Strip UNSIGNED and ZEROFILL
    q = q.replace(/\bUNSIGNED\b/gi, '');
    q = q.replace(/\bZEROFILL\b/gi, '');

    // Replace ON UPDATE CURRENT_TIMESTAMP
    q = q.replace(/ON\s+UPDATE\s+CURRENT_TIMESTAMP/gi, '');

    // Strip MySQL table options at end of statement: ENGINE=..., DEFAULT CHARSET=..., COLLATE=..., AUTO_INCREMENT=...
    q = q.replace(/ENGINE\s*=\s*\w+/gi, '')
         .replace(/DEFAULT\s+CHARSET\s*=\s*[\w\d]+/gi, '')
         .replace(/CHARSET\s*=\s*[\w\d]+/gi, '')
         .replace(/COLLATE\s*=\s*[\w\d]+/gi, '')
         .replace(/AUTO_INCREMENT\s*=\s*\d+/gi, '');

    // Convert MySQL backticks `table` / `col` to standard quotes
    q = q.replace(/`([^`]+)`/g, '"$1"');
  }

  // 6. MySQL LIMIT count, offset -> LIMIT count OFFSET offset
  q = q.replace(/\bLIMIT\s+(\d+)\s*,\s*(\d+)/gi, 'LIMIT $2 OFFSET $1');

  // 7. Replace NOW() function with CURRENT_TIMESTAMP
  q = q.replace(/\bNOW\(\)/gi, 'CURRENT_TIMESTAMP');

  return q;
}

function preprocessQuery(query) {
  if (!query) return query;
  return translateMysqlToSqlite(query);
}

export async function executeQuery(query) {
  let success = false;
  let result = null;
  let error = null;
  let executionTime = 0;
  let affectedRows = 0; // Initialize affectedRows

  const processedQuery = preprocessQuery(query);

  try {
    const start = performance.now();
    result = db.exec(processedQuery);
    affectedRows = db.getRowsModified(); // Get affected rows for DML statements
    await saveDatabase(); // persist changes
    const end = performance.now();
    success = true;
    executionTime = (end - start).toFixed(2);
    console.log("SQL execution successful. Result:", result, "Affected Rows:", affectedRows);
    // The history saving logic is now outside the try-catch to ensure it always runs
  } catch (err) {
    error = err.message;
    console.error("SQL execution error:", err.message);
  }

  // Save query to history (moved outside try-catch to ensure it always runs)
  const idb = await openDB(DB_NAME, DB_VERSION);
  const historyItem = {
    query,
    executed_at: new Date().toISOString(),
    success,
    error_message: error,
    execution_time_ms: parseFloat(executionTime),
  };
  await idb.add(DB_STORE_HISTORY, historyItem);
  console.log("History item added to IndexedDB:", historyItem);

  return { success, result, error, executionTime, affectedRows };
}

export async function loadHistoryFromIndexedDB() {
  const idb = await openDB(DB_NAME, DB_VERSION);
  const history = await idb.getAll(DB_STORE_HISTORY);
  console.log("History loaded from IndexedDB:", history);
  return history;
}

export async function clearHistoryInIndexedDB() {
  const idb = await openDB(DB_NAME, DB_VERSION);
  await idb.clear(DB_STORE_HISTORY);
  console.log("History cleared from IndexedDB.");
}

export async function saveQueryToIndexedDB({ query, label }, databaseInstance = db) {
  const idb = await openDB(DB_NAME, DB_VERSION);
  const savedItem = {
    query,
    label: label || 'Saved Query',
    saved_at: new Date().toISOString(),
  };
  const id = await idb.add(DB_STORE_SAVED, savedItem);
  savedItem.id = id;

  // Sync to SQLite table
  const targetDb = databaseInstance || db;
  if (targetDb) {
    ensureSavedQueriesSqliteTable(targetDb);
    try {
      const escapedQuery = query.replace(/'/g, "''");
      const escapedLabel = (label || 'Saved Query').replace(/'/g, "''");
      targetDb.exec(
        `INSERT INTO _datapilot_saved_queries (id, query, label, saved_at) VALUES (${id}, '${escapedQuery}', '${escapedLabel}', '${savedItem.saved_at}');`
      );
      await saveDatabase();
    } catch (err) {
      console.error("Error inserting saved query into SQLite table:", err);
    }
  }

  return savedItem;
}

export async function loadSavedQueriesFromIndexedDB() {
  const idb = await openDB(DB_NAME, DB_VERSION);
  const saved = await idb.getAll(DB_STORE_SAVED);
  return saved;
}

export async function deleteSavedQueryFromIndexedDB(id, databaseInstance = db) {
  const idb = await openDB(DB_NAME, DB_VERSION);
  await idb.delete(DB_STORE_SAVED, id);

  // Delete from SQLite table
  const targetDb = databaseInstance || db;
  if (targetDb) {
    try {
      ensureSavedQueriesSqliteTable(targetDb);
      targetDb.exec(`DELETE FROM _datapilot_saved_queries WHERE id = ${id};`);
      await saveDatabase();
    } catch (err) {
      console.error("Error deleting saved query from SQLite table:", err);
    }
  }
}

export async function updateSavedQueryLabelInIndexedDB(id, label, databaseInstance = db) {
  const idb = await openDB(DB_NAME, DB_VERSION);
  const item = await idb.get(DB_STORE_SAVED, id);
  if (item) {
    item.label = label;
    await idb.put(DB_STORE_SAVED, item);
  }

  // Update in SQLite table
  const targetDb = databaseInstance || db;
  if (targetDb) {
    try {
      ensureSavedQueriesSqliteTable(targetDb);
      const escapedLabel = (label || '').replace(/'/g, "''");
      targetDb.exec(
        `UPDATE _datapilot_saved_queries SET label = '${escapedLabel}' WHERE id = ${id};`
      );
      await saveDatabase();
    } catch (err) {
      console.error("Error updating saved query label in SQLite table:", err);
    }
  }
}

export async function syncSavedQueriesFromSqlite(databaseInstance = db) {
  const targetDb = databaseInstance || db;
  if (!targetDb) return [];
  try {
    ensureSavedQueriesSqliteTable(targetDb);
    const res = targetDb.exec("SELECT id, query, label, saved_at FROM _datapilot_saved_queries ORDER BY id ASC;");
    if (res && res[0] && res[0].values) {
      const sqliteSaved = res[0].values.map(row => ({
        id: row[0],
        query: row[1],
        label: row[2],
        saved_at: row[3]
      }));

      // Update IndexedDB with these items
      const idb = await openDB(DB_NAME, DB_VERSION);
      await idb.clear(DB_STORE_SAVED);
      for (const item of sqliteSaved) {
        await idb.put(DB_STORE_SAVED, item);
      }
      return sqliteSaved;
    }
  } catch (err) {
    console.error("Error syncing saved queries from SQLite:", err);
  }
  return await loadSavedQueriesFromIndexedDB();
}

